import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronLeft, ChevronRight, Calendar, X, Check } from 'lucide-react';
import { Button } from './ui/button';
import { getTodayString, formatDateString } from '../utils/dateUtils';

/**
 * WeekDateSelector - Shared component for week-based date selection
 * Used by: Grocery, PlanFoodLauncher, MealView Add to Plan
 * 
 * Props:
 * - selectedDates: array of selected date strings
 * - onDatesChange: callback when dates change
 * - mode: 'single' | 'multiple' | 'range' - selection mode
 * - allowRange: boolean - show custom date range option
 * - showApplyButton: boolean - show Apply/Cancel buttons
 * - onApply: callback when Apply is clicked
 * - onCancel: callback when Cancel is clicked
 */
function WeekDateSelector({ 
  selectedDates = [], 
  onDatesChange, 
  mode = 'multiple',
  allowRange = true,
  showApplyButton = true,
  onApply,
  onCancel,
  className = ''
}) {
  // Week offset from current week (0 = current, -1 = previous, 1 = next)
  var [weekOffset, setWeekOffset] = useState(0);
  var [showRangeInput, setShowRangeInput] = useState(false);
  var [rangeStart, setRangeStart] = useState('');
  var [rangeEnd, setRangeEnd] = useState('');
  
  var today = getTodayString();
  
  // Get dates for displayed week based on offset
  function getWeekDates(offset) {
    var now = new Date();
    var dayOfWeek = now.getDay();
    // Start from Sunday of current week
    var startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - dayOfWeek + (offset * 7));
    
    var dates = [];
    for (var i = 0; i < 7; i++) {
      var d = new Date(startOfWeek);
      d.setDate(startOfWeek.getDate() + i);
      dates.push(formatDateString(d));
    }
    return dates;
  }
  
  var displayedDates = getWeekDates(weekOffset);
  
  // Format date for display
  function formatDayLabel(dateStr) {
    var d = new Date(dateStr + 'T00:00:00');
    return d.toLocaleDateString('en-US', { weekday: 'short' });
  }
  
  function formatMonthYear() {
    var firstDate = new Date(displayedDates[0] + 'T00:00:00');
    var lastDate = new Date(displayedDates[6] + 'T00:00:00');
    
    if (firstDate.getMonth() === lastDate.getMonth()) {
      return firstDate.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
    }
    return firstDate.toLocaleDateString('en-US', { month: 'short' }) + ' - ' + 
           lastDate.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
  }
  
  function isToday(dateStr) {
    return dateStr === today;
  }
  
  function isSelected(dateStr) {
    return selectedDates.includes(dateStr);
  }
  
  // Toggle date selection
  function toggleDate(dateStr) {
    if (mode === 'single') {
      onDatesChange([dateStr]);
    } else {
      if (selectedDates.includes(dateStr)) {
        onDatesChange(selectedDates.filter(function(d) { return d !== dateStr; }));
      } else {
        onDatesChange(selectedDates.concat([dateStr]));
      }
    }
  }
  
  // Select full week
  function selectFullWeek() {
    var newDates = selectedDates.slice();
    var allSelected = displayedDates.every(function(d) { return selectedDates.includes(d); });
    
    if (allSelected) {
      // Deselect all in this week
      newDates = selectedDates.filter(function(d) { return !displayedDates.includes(d); });
    } else {
      // Add all dates from this week
      for (var i = 0; i < displayedDates.length; i++) {
        if (!newDates.includes(displayedDates[i])) {
          newDates.push(displayedDates[i]);
        }
      }
    }
    onDatesChange(newDates.sort());
  }
  
  // Apply date range
  function applyRange() {
    if (!rangeStart || !rangeEnd) return;
    
    var start = new Date(rangeStart);
    var end = new Date(rangeEnd);
    
    if (start > end) {
      var temp = start;
      start = end;
      end = temp;
    }
    
    var rangeDates = [];
    var current = new Date(start);
    while (current <= end) {
      rangeDates.push(formatDateString(current));
      current.setDate(current.getDate() + 1);
    }
    
    onDatesChange(rangeDates);
    setShowRangeInput(false);
    setRangeStart('');
    setRangeEnd('');
  }
  
  // Navigate weeks
  function goToPreviousWeek() {
    setWeekOffset(weekOffset - 1);
  }
  
  function goToNextWeek() {
    setWeekOffset(weekOffset + 1);
  }
  
  function goToCurrentWeek() {
    setWeekOffset(0);
  }
  
  // Get week range label
  function getWeekRangeLabel() {
    var first = new Date(displayedDates[0] + 'T00:00:00');
    var last = new Date(displayedDates[6] + 'T00:00:00');
    var opts = { month: 'short', day: 'numeric' };
    return first.toLocaleDateString('en-US', opts) + ' - ' + last.toLocaleDateString('en-US', opts);
  }
  
  // Check if all week dates are selected
  var isFullWeekSelected = displayedDates.every(function(d) { return selectedDates.includes(d); });
  
  return (
    <div className={'space-y-4 ' + className}>
      {/* Week Navigation Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={goToPreviousWeek}
          className="p-2 rounded-full hover:bg-secondary transition-colors"
          data-testid="prev-week-btn"
        >
          <ChevronLeft size={20} />
        </button>
        
        <div className="text-center">
          <p className="font-medium text-sm">{formatMonthYear()}</p>
          <p className="text-xs text-muted-foreground">{getWeekRangeLabel()}</p>
        </div>
        
        <button
          onClick={goToNextWeek}
          className="p-2 rounded-full hover:bg-secondary transition-colors"
          data-testid="next-week-btn"
        >
          <ChevronRight size={20} />
        </button>
      </div>
      
      {/* Week Days Grid */}
      <div className="grid grid-cols-7 gap-1">
        {displayedDates.map(function(dateStr) {
          var dayNum = new Date(dateStr + 'T00:00:00').getDate();
          var selected = isSelected(dateStr);
          var isCurrentDay = isToday(dateStr);
          
          return (
            <button
              key={dateStr}
              onClick={function() { toggleDate(dateStr); }}
              className={'flex flex-col items-center py-2 px-1 rounded-lg transition-all ' + (
                selected 
                  ? 'bg-accent text-white' 
                  : isCurrentDay 
                    ? 'bg-accent/10 border border-accent/30' 
                    : 'hover:bg-secondary'
              )}
              data-testid={'date-' + dateStr}
            >
              <span className="text-[10px] font-medium opacity-70">{formatDayLabel(dateStr)}</span>
              <span className={'text-lg font-bold ' + (selected ? '' : isCurrentDay ? 'text-accent' : '')}>
                {dayNum}
              </span>
              {isCurrentDay && !selected && (
                <span className="w-1 h-1 rounded-full bg-accent mt-0.5"></span>
              )}
            </button>
          );
        })}
      </div>
      
      {/* Quick Actions */}
      <div className="flex items-center gap-2">
        {weekOffset !== 0 && (
          <Button
            onClick={goToCurrentWeek}
            variant="ghost"
            size="sm"
            className="text-xs h-8"
          >
            Go to Today
          </Button>
        )}
        
        {mode === 'multiple' && (
          <Button
            onClick={selectFullWeek}
            variant="outline"
            size="sm"
            className={'text-xs h-8 ' + (isFullWeekSelected ? 'bg-accent/10 border-accent/30' : '')}
            data-testid="full-week-btn"
          >
            {isFullWeekSelected ? <Check size={12} className="mr-1" /> : null}
            Full Week
          </Button>
        )}
        
        {allowRange && (
          <Button
            onClick={function() { setShowRangeInput(!showRangeInput); }}
            variant="outline"
            size="sm"
            className="text-xs h-8"
            data-testid="custom-range-btn"
          >
            <Calendar size={12} className="mr-1" />
            Custom Range
          </Button>
        )}
      </div>
      
      {/* Custom Date Range Input */}
      <AnimatePresence>
        {showRangeInput && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="overflow-hidden"
          >
            <div className="p-4 bg-secondary/30 rounded-xl space-y-3">
              <div className="flex items-center justify-between">
                <p className="text-sm font-medium">Custom Date Range</p>
                <button 
                  onClick={function() { setShowRangeInput(false); }}
                  className="p-1 rounded-full hover:bg-secondary"
                >
                  <X size={14} />
                </button>
              </div>
              
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">Start Date</label>
                  <input
                    type="date"
                    value={rangeStart}
                    onChange={function(e) { setRangeStart(e.target.value); }}
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm"
                    data-testid="range-start-input"
                  />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground mb-1 block">End Date</label>
                  <input
                    type="date"
                    value={rangeEnd}
                    onChange={function(e) { setRangeEnd(e.target.value); }}
                    className="w-full h-10 px-3 rounded-lg border border-border bg-background text-sm"
                    data-testid="range-end-input"
                  />
                </div>
              </div>
              
              <Button
                onClick={applyRange}
                disabled={!rangeStart || !rangeEnd}
                className="w-full h-10 rounded-lg bg-accent text-white"
                data-testid="apply-range-btn"
              >
                Apply Range
              </Button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
      
      {/* Selected dates summary */}
      {selectedDates.length > 0 && (
        <div className="text-center">
          <p className="text-xs text-muted-foreground">
            {selectedDates.length} day{selectedDates.length !== 1 ? 's' : ''} selected
          </p>
        </div>
      )}
      
      {/* Apply/Cancel Buttons */}
      {showApplyButton && (
        <div className="flex gap-3 pt-2">
          {onCancel && (
            <Button
              onClick={onCancel}
              variant="outline"
              className="flex-1 h-11 rounded-xl"
              data-testid="date-cancel-btn"
            >
              Cancel
            </Button>
          )}
          <Button
            onClick={onApply}
            disabled={selectedDates.length === 0}
            className="flex-1 h-11 rounded-xl bg-accent text-white"
            data-testid="date-apply-btn"
          >
            Apply ({selectedDates.length} day{selectedDates.length !== 1 ? 's' : ''})
          </Button>
        </div>
      )}
    </div>
  );
}

export default WeekDateSelector;
